"use client";

import Image from "next/image";
import { Reveal } from "@/components/ui/Reveal";

export type PublishingWay = {
  title: string;
  description: string;
  iconSrc: string;
};

const defaultWays: PublishingWay[] = [
  {
    title: "Traditional\nPublishing",
    description:
      "Let us handle the heavy lifting. From editing and design to distribution, your book gets full professional support. You focus on writing, while we make sure it reaches readers through established channels. Perfect for authors seeking credibility, wide reach, and guidance at every step.",
    iconSrc: "/landing/ways/traditional.webp",
  },
  {
    title: "Self -\nPublishing",
    description:
      "Take the reins. You decide the timeline, design, and every creative choice. Keep more royalties and maintain complete control. Ideal for authors who want flexibility, independence, and the freedom to publish on their own terms.",
    iconSrc: "/landing/ways/self.webp",
  },
  {
    title: "Hybrid\nPublishing",
    description:
      "Combine the best of both worlds. Receive professional help with editing, design, and distribution while keeping more control and royalties. Great for authors who want support, reach, and ownership, without giving up their vision.",
    iconSrc: "/landing/ways/hybrid.webp",
  },
];

export type PublishingWaysSectionProps = {
  ways?: PublishingWay[];
  className?: string;
};

export function PublishingWaysSection({
  ways = defaultWays,
  className,
}: PublishingWaysSectionProps) {
  return (
    <section
      className={[
        "relative bg-cover bg-center py-12 lg:py-[50px]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        backgroundImage:
          "linear-gradient(rgba(30,24,18,0.72), rgba(30,24,18,0.78)), url('/landing/ways/book-bg.webp')",
      }}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-8 max-w-4xl text-center text-white">
          <Reveal>
            <h2 className="font-heading text-3xl font-semibold text-white sm:text-4xl lg:text-[45px] lg:leading-[54px]">
              Find The Best Way To Publish Your Book
            </h2>
          </Reveal>
          <Reveal delayMs={150}>
            <p className="mt-4 text-base leading-relaxed text-white/90">
              Bringing your story to readers shouldn&apos;t be complicated. We
              guide authors from manuscript to launch, helping your book reach
              the right audience and leave a lasting impression.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3 xl:gap-8">
          {ways.map((way, index) => (
            <Reveal key={way.title} delayMs={index * 150} className="h-full">
              <article className="publish-way-card market-box group relative h-full rounded-[15px] bg-white p-6 text-[#29361a] sm:p-8">
                <div className="relative z-1 mb-4 flex items-center gap-3">
                  <Image
                    src={way.iconSrc}
                    alt=""
                    aria-hidden="true"
                    width={70}
                    height={70}
                    sizes="70px"
                    quality={60}
                    className="h-[70px] w-[70px] object-contain transition duration-300"
                  />
                  <h3 className="font-heading text-xl font-semibold leading-tight whitespace-pre-line text-[#29361a] transition-colors duration-300 sm:text-2xl">
                    {way.title}
                  </h3>
                </div>
                <p className="relative z-1 text-sm leading-relaxed text-[#29361a] transition-colors duration-300 sm:text-base">
                  {way.description}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
